import type { BadgeProps } from './Badge.types';

const baseClasses =
  'tw:inline-flex tw:items-center tw:rounded-[3px] tw:px-[5px] tw:py-[2px] tw:text-[0.8rem] tw:whitespace-nowrap tw:italic';

// preview and success intentionally share the same green styling right now. Success has no visual identity
// of its own yet, so it borrows preview's rather than introducing a second, near-identical green. They are
// kept as separate variantClasses entries (not a single shared variant) specifically so either can diverge
// later by editing its own line here, without touching any call site that already passed variant="success"
// or variant="preview".
const greenBadgeClasses =
  'tw:bg-sq-badge-preview-bg tw:dark:bg-sq-bg-default-preview-dark tw:text-sq-text-interactive tw:dark:text-sq-text-interactive-dark';

const variantClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
  new: 'tw:bg-sq-warning-bg-color tw:dark:bg-sq-text-dark-warning-color tw:text-sq-text-dark-warning-color tw:dark:text-sq-warning-bg-color',
  preview: greenBadgeClasses,
  success: greenBadgeClasses,
  neutral:
    'tw:bg-sq-badge-neutral-bg tw:dark:bg-sq-badge-neutral-bg-dark/40 tw:border tw:border-transparent tw:text-sq-gray-text',
  error: 'tw:bg-sq-badge-error-bg tw:dark:bg-sq-badge-error-bg-dark tw:text-sq-danger',
};

// The solid leading segment used when a badge carries a label. Kept beside variantClasses so a variant's two
// halves are always changed together, and following the same success/preview separation explained above.
const solidLabelClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
  new: 'tw:bg-sq-warning tw:text-sq-black',
  preview: 'tw:bg-sq-text-interactive tw:text-sq-white tw:dark:bg-sq-text-interactive-dark tw:dark:text-sq-black',
  success: 'tw:bg-sq-text-interactive tw:text-sq-white tw:dark:bg-sq-text-interactive-dark tw:dark:text-sq-black',
  neutral: 'tw:bg-sq-muted tw:text-sq-white',
  error: 'tw:bg-sq-danger tw:text-sq-white',
};

const labelBorderClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
  new: 'tw:border-sq-warning/40',
  preview: 'tw:border-sq-text-interactive/40',
  success: 'tw:border-sq-text-interactive/40',
  neutral: 'tw:border-sq-muted/40',
  error: 'tw:border-sq-danger/40',
};

/**
 * Renders a compact inline badge for feature-state labels such as new or preview.
 *
 * With a `label`, the badge grows a solid tag on its left naming what it reports on; the tag squares off the
 * badge's left corners so the two segments read as one pill.
 */
export const Badge = ({ children, variant = 'new', label, testId, extraClassNames = '' }: BadgeProps) => {
  const appliedClasses = `${baseClasses} ${variantClasses[variant]} ${extraClassNames}`.trim();

  if (label === undefined || label === null) {
    return (
      <span data-testid={testId} className={appliedClasses}>
        {children}
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      className={`tw:inline-flex tw:items-stretch tw:overflow-hidden tw:rounded-sm tw:border ${labelBorderClasses[variant]}`}>
      <span className={`${baseClasses} tw:font-bold tw:not-italic ${solidLabelClasses[variant]}`}>{label}</span>
      <span className={`${appliedClasses} tw:rounded-l-none`}>{children}</span>
    </span>
  );
};

export default Badge;
